import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BaseListingProvider } from './base-listing.provider';
import { NotificationFilters } from 'src/shared/models/notifications/notification-filters';
import { News } from 'src/shared/models/notifications/news.model';
import { LocalStorageService } from './local-storage.service';
import { AuthenticatedHttpService, OfflineWrapResult } from './authenticatedHttpService.service';
import { NotificationsResponse } from 'src/shared/models/notifications/notifications-response';
import { environment } from 'src/environments/environment';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { Reply } from 'src/shared/models/notifications/reply.model';
import * as fp from 'lodash/fp';
import { formatUtcToLocal } from 'src/shared/utils/date-utils';

@Injectable({ providedIn: 'root' })
export class NewsProvider extends BaseListingProvider<NotificationFilters, News> {
  static readonly INCOMING = 'NewsProvider_INCOMING';

  protected override get endpoint(): string {
    return 'me/notifications-incoming';
  }
  protected override get getErrorMessage(): string {
    return 'Error_Loading_News';
  }
  protected override get storageKeys() {
    return [NewsProvider.INCOMING];
  }
  protected override getStorageKey(filters: NotificationFilters = this._filters.value): string {
    return NewsProvider.INCOMING;
  }

  public readonly _unreadCount = signal<number | null>(null);
  public readonly unreadCount = this._unreadCount.asReadonly();
  public readonly unreadCount$ = toObservable(this._unreadCount);

  initUnreadCountRequired: boolean | null = true;

  setUnreadCount(value: number | null): void {
    this._unreadCount.set(value);
  }

  constructor(
    protected override http: AuthenticatedHttpService,
    protected override storage: LocalStorageService
  ) {
    super(http, storage, new NotificationFilters());
  }

  async fetchUnreadCount() {
    try {
      this.initUnreadCountRequired = null;

      const resp = await this.http.wrapIntoOffline<NotificationsResponse | number>({
        fetch: this.http.get,
        params: [`${environment.web.apiUrl}me/notifications/unread-count`],
        storeKey: NewsProvider.INCOMING,
        renew: false,
      });

      if (resp.status === OfflineWrapResult.Offline) {
        // Offline result has its own structure
        this.initUnreadCountRequired = true; // it is used as fragment from different query
        this._unreadCount.set(fp.get('result.unreadCount', resp)); // its so to store unread count is single place
      } else {
        // case of success or error
        this._unreadCount.set(fp.get('result', resp) as number | null);
      }

      if (resp.status === OfflineWrapResult.Error) {
        this.initUnreadCountRequired = true;
        throw resp.error;
      }
    } catch (error) {
      console.error(error);
    }
  }

  // @ts-ignore TS wont recognize that NotificationsResponse is direct child of PagedResponse<TItem>
  async fetch(filters = this.initialFilter, append = false) {
    const res = await super.fetch<NotificationsResponse>(filters, append);

    this._unreadCount.set(res.result?.unreadCount || null);

    return res;
  }

  async read(notificationId: string) {
    const statusWasChanged = await this.readLocal(notificationId);

    try {
      return await this.http.put(
        `${environment.web.apiUrl}notifications/${notificationId}/read-with-replies`,
        null
      );
    } catch (error) {
      if (!HttpUtils.isTimeout(error)) {
        if (statusWasChanged) {
          await this.readLocal(notificationId, false);
        }
      }

      throw error;
    }
  }

  private async readLocal(notificationId: string, read = true) {
    const notifs = [...(this._items.value || [])];
    const readNotif = notifs.find((n) => n.id === notificationId);
    const alreadyMarked = fp.get('read', readNotif) === read;
    if (readNotif && !alreadyMarked) {
      // Covering edge cases
      readNotif.read = read;
      this._items.next(notifs);
      this._unreadCount.update((count) => (count ?? 0) + (read ? -1 : 1));

      const stored: NotificationsResponse | null = await this.storage.get(NewsProvider.INCOMING); // As that only applicable to incoming
      stored!.unreadCount = this._unreadCount()!;
      stored!.items = notifs; // Current notifs are shown and they're stored
      await this.storage.set(NewsProvider.INCOMING, stored);
    }

    return readNotif && !alreadyMarked;
  }

  private async answeredLocal(notificationId: string, answered = true) {
    const notifs = [...(this._items.value || [])];
    const answeredNotif = notifs.find((n) => n.id === notificationId);
    const alreadyAnswered = fp.get('answered', answeredNotif) === answered;
    if (answeredNotif && !alreadyAnswered) {
      // Covering edge cases
      answeredNotif.answered = answered;
      this._items.next(notifs);

      const stored: NotificationsResponse | null = await this.storage.get(NewsProvider.INCOMING); // As that only applicable to incoming
      stored!.unreadCount = this._unreadCount()!;
      stored!.items = notifs; // Current notifs are shown and they're stored
      await this.storage.set(NewsProvider.INCOMING, stored);
    }

    return answeredNotif && !alreadyAnswered;
  }

  async getById(notificationId: string) {
    try {
      return await this.http.get(`${environment.web.apiUrl}notifications/${notificationId}`);
    } catch (error) {
      console.log(error);
    }
  }

  async getRepliesById(notificationId: string): Promise<News[] | null> {
    try {
      return await this.http.get(
        `${environment.web.apiUrl}me/replies?notificationIds=${notificationId}`
      );
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  getCreationDate(item: News | Reply): string {
    const timeOrNow = fp.getOr(new Date(), 'creationDate', item);
    return formatUtcToLocal(timeOrNow, 'DD.MM.YYYY HH:mm');
  }

  async reply(model: Reply) {
    const statusWasChanged = await this.answeredLocal(model.topic);

    try {
      const res: Response = await this.http.post(
        environment.web.apiUrl + 'notifications/reply',
        JSON.stringify(model, this.replacer)
      );

      return res;
    } catch (error) {
      console.error(error);
      if (!HttpUtils.isTimeout(error)) {
        if (statusWasChanged) {
          await this.readLocal(model.databaseIdentifier, false);
        }
      }

      throw error;
    }
  }

  private replacer(key: any, value: any) {
    if (value === null) {
      return undefined;
    }
    return value;
  }

  override async purge() {
    this._unreadCount.set(null);
    await super.purge();
  }
}
